import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private userPermissionsSubject = new BehaviorSubject<string[]>([]);
  public userPermissions$ = this.userPermissionsSubject.asObservable();
  private permissionsCache: string[] | null = null;
  private isSysAdmin: boolean = false;
  private lastTokenSnapshot: string | null = null;

  constructor(
    private authService: AuthService
  ) {
    this.lastTokenSnapshot = this.authService.getToken();
    this.loadUserPermissions();
    this.checkSysAdminRole();

    // Sincroniza permissões sempre que login/logout altera a sessão
    this.authService.isAuthenticated$.subscribe(() => {
      this.lastTokenSnapshot = this.authService.getToken();
      this.reloadPermissions();
    });
  }

  /**
   * Verifica se o usuário tem o perfil SysAdmin
   * Verifica no token JWT e no localStorage
   */
  private checkSysAdminRole(): void {
    this.isSysAdmin = false;

    // `user_data` no localStorage tem precedência sobre o JWT — assim o
    // MockSessionService consegue alternar a sessão durante impersonation
    // (sem mexer no token real) e o PermissionService respeita.
    let userDataChecked = false;
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const user = JSON.parse(userData);
        userDataChecked = true;
        this.isSysAdmin = this.checkRoleInPayload(user);
        if (this.isSysAdmin) return;
      }
    } catch {
      // ignore — segue pro fallback do token
    }

    // Se `user_data` define explicitamente um role não-sysadmin, NÃO sobe
    // pro token (impersonação ativa).
    if (userDataChecked) return;

    const token = this.authService.getToken();
    if (!token) return;

    try {
      const payload = this.decodeJwtToken(token);
      if (payload) {
        this.isSysAdmin = this.checkRoleInPayload(payload);
      }
    } catch (error) {
      console.warn('Erro ao verificar role SysAdmin no token:', error);
    }
  }

  /**
   * Verifica se o payload contém o role SysAdmin
   * @param payload - Objeto que pode conter informações de role
   * @returns true se encontrar SysAdmin no payload
   */
  private checkRoleInPayload(payload: any): boolean {
    // Verificar diferentes possíveis propriedades onde o role pode estar
    const role = payload.role || payload.roles || payload.Role || payload.Roles;
    const roleName = payload.roleName || payload.role_name;
    
    // Verificar role como string ou array de strings
    if (role) {
      if (Array.isArray(role)) {
        // Se for array, verificar cada item
        for (const r of role) {
          if (typeof r === 'string') {
            if (this.matchesSysAdminRole(r)) {
              return true;
            }
          } else if (r && typeof r === 'object') {
            // Se for objeto, verificar propriedades name, role, Role
            const name = r.name || r.role || r.Role || r.Name;
            if (name && this.matchesSysAdminRole(String(name))) {
              return true;
            }
          }
        }
      } else if (typeof role === 'string') {
        if (this.matchesSysAdminRole(role)) {
          return true;
        }
      }
    }
    
    // Verificar roleName
    if (roleName && this.matchesSysAdminRole(String(roleName))) {
      return true;
    }
    
    // Verificar em arrays específicos de roles
    const userRoles = payload.userRoles || payload.user_roles;
    if (Array.isArray(userRoles)) {
      for (const r of userRoles) {
        if (typeof r === 'string') {
          if (this.matchesSysAdminRole(r)) {
            return true;
          }
        } else if (r && typeof r === 'object') {
          const name = r.name || r.role || r.Role || r.Name || r;
          if (name && this.matchesSysAdminRole(String(name))) {
            return true;
          }
        }
      }
    }
    
    return false;
  }

  /**
   * Valida se um nome de role representa SysAdmin sem falso positivo por substring.
   */
  private matchesSysAdminRole(roleValue: string): boolean {
    if (!roleValue) {
      return false;
    }

    const normalized = roleValue.trim().toLowerCase();

    return (
      normalized === 'sysadmin' ||
      normalized === 'systemadministrator' ||
      /(^|[\W_])sysadmin($|[\W_])/i.test(normalized) ||
      /(^|[\W_])systemadministrator($|[\W_])/i.test(normalized)
    );
  }

  /**
   * Carrega as permissões do usuário logado
   */
  private loadUserPermissions(): void {
    const token = this.authService.getToken();
    if (!token) {
      this.userPermissionsSubject.next([]);
      return;
    }

    let tokenPermissions: string[] = [];

    // Tentar decodificar o token JWT para obter as permissões
    try {
      const payload = this.decodeJwtToken(token);
      if (payload) {
        // Verificar diferentes possíveis propriedades onde as permissões podem estar
        const claims =
          payload.claims ||
          payload.permissions ||
          payload.permission ||
          payload.Permissions ||
          payload.Permission ||
          payload.Claims ||
          [];
        tokenPermissions = this.normalizePermissions(claims);
      }
    } catch (error) {
      console.warn('Erro ao decodificar token JWT:', error);
    }

    // Tentar permissões completas salvas no login (preferência para claims namespaced)
    const storedPermissions = localStorage.getItem('user_permissions');
    if (storedPermissions) {
      try {
        const parsedPermissions = JSON.parse(storedPermissions);
        const normalizedStored = this.normalizePermissions(parsedPermissions);
        if (normalizedStored.length > 0) {
          this.permissionsCache = normalizedStored;
          this.userPermissionsSubject.next(normalizedStored);
          return;
        }
      } catch (error) {
        console.warn('Erro ao parsear permissões do localStorage:', error);
      }
    }
    
    // Fallback para permissões do token (alguns backends enviam apenas actions genéricas)
    if (tokenPermissions.length > 0) {
      this.permissionsCache = tokenPermissions;
      this.userPermissionsSubject.next(tokenPermissions);
      return;
    }

    // Se não encontrar em nenhum lugar, retornar array vazio
    this.permissionsCache = [];
    this.userPermissionsSubject.next([]);
  }

  /**
   * Decodifica um token JWT
   * @param token - Token JWT a ser decodificado
   * @returns Payload do token ou null em caso de erro
   */
  private decodeJwtToken(token: string): any | null {
    if (!token || typeof token !== 'string') {
      return null;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Token JWT inválido: formato incorreto');
        return null;
      }

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Erro ao decodificar token JWT:', error);
      return null;
    }
  }

  /**
   * Retorna `true` quando o usuário logado tem o perfil SysAdmin.
   * Usado por telas que precisam liberar comandos/visualizações restritos
   * (ex.: aba "Desenvolvedor" no editor de formulários).
   */
  isAdmin(): boolean {
    this.syncPermissionsWithCurrentSession();
    return this.isSysAdmin;
  }

  /**
   * Verifica se o usuário tem uma permissão específica
   * @param permission - A permissão a ser verificada (ex: 'customer.permission.list2')
   * @returns true se o usuário é SysAdmin ou tem a permissão específica
   */
  hasPermission(permission: string): boolean {
    this.syncPermissionsWithCurrentSession();
    
    // SysAdmin tem acesso a tudo
    if (this.isSysAdmin) {
      return true;
    }
    
    if (!permission) return false;
    
    const permissions = this.permissionsCache || this.userPermissionsSubject.value;
    return permissions.includes(this.normalizePermission(permission));
  }

  /**
   * Verifica se o usuário tem pelo menos uma das permissões fornecidas
   * @param permissions - Array de permissões a serem verificadas
   * @returns true se o usuário é SysAdmin ou tem pelo menos uma das permissões
   */
  hasAnyPermission(permissions: string[]): boolean {
    this.syncPermissionsWithCurrentSession();

    // SysAdmin tem acesso a tudo
    if (this.isSysAdmin) {
      return true;
    }
    
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Verifica se o usuário tem todas as permissões fornecidas
   * @param permissions - Array de permissões a serem verificadas
   * @returns true se o usuário é SysAdmin ou tem todas as permissões
   */
  hasAllPermissions(permissions: string[]): boolean {
    this.syncPermissionsWithCurrentSession();

    // SysAdmin tem acesso a tudo
    if (this.isSysAdmin) {
      return true;
    }
    
    if (!permissions || permissions.length === 0) return false;
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Obtém todas as permissões do usuário atual
   */
  getUserPermissions(): string[] {
    this.syncPermissionsWithCurrentSession();
    return this.permissionsCache || this.userPermissionsSubject.value;
  }

  /**
   * Recarrega as permissões do usuário
   */
  reloadPermissions(): void {
    this.permissionsCache = null;
    this.loadUserPermissions();
    this.checkSysAdminRole();
  }

  /**
   * Verifica se o usuário atual é SysAdmin
   * @returns true se o usuário é SysAdmin
   */
  isUserSysAdmin(): boolean {
    this.syncPermissionsWithCurrentSession();
    return this.isSysAdmin;
  }

  /**
   * Sincroniza permissões quando o token muda (login, logout ou troca de usuário)
   */
  private syncPermissionsWithCurrentSession(): void {
    const currentToken = this.authService.getToken();
    if (currentToken !== this.lastTokenSnapshot) {
      this.lastTokenSnapshot = currentToken;
      this.reloadPermissions();
    }
  }

  /**
   * Normaliza uma permissão para comparação consistente.
   */
  private normalizePermission(permission: string): string {
    return String(permission || '').trim().toLowerCase();
  }

  /**
   * Extrai e normaliza permissões vindas da API/token/localStorage.
   * Suporta array de strings ou array de objetos.
   */
  private normalizePermissions(rawPermissions: unknown): string[] {
    if (!Array.isArray(rawPermissions)) {
      return [];
    }

    const normalized = rawPermissions
      .map((item) => {
        if (typeof item === 'string') {
          return this.normalizePermission(item);
        }

        if (item && typeof item === 'object') {
          const candidate =
            (item as any).id ||
            (item as any).claim ||
            (item as any).permission ||
            (item as any).value ||
            (item as any).name;

          if (typeof candidate === 'string') {
            return this.normalizePermission(candidate);
          }
        }

        return '';
      })
      .filter((permission) => permission.length > 0);

    return Array.from(new Set(normalized));
  }
}

